import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton
} from '@mui/material';
import { DragIndicator, Close } from '@mui/icons-material';

interface LegendItem {
  color: string;
  label: string;
  value?: string | number;
}

interface DraggableLegendProps {
  title: string;
  items: LegendItem[];
  isVisible: boolean;
  onClose: () => void;
  position?: { x: number; y: number };
  onPositionChange?: (position: { x: number; y: number }) => void;
}

const DraggableLegend = ({
  title,
  items,
  isVisible,
  onClose,
  position = { x: 20, y: 20 },
  onPositionChange,
}: DraggableLegendProps) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = React.useState(position);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y,
    });
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    };

    setCurrentPosition(newPosition);
    onPositionChange?.(newPosition);
  }, [isDragging, dragStart, onPositionChange]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  if (!isVisible) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: currentPosition.x,
        top: currentPosition.y,
        zIndex: 1000,
        minWidth: 200,
        maxWidth: 300,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DragIndicator color="action" />
          <Typography variant="subtitle2" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ padding: 2 }}>
        {items.map((item) => (
          <Box
            key={`${item.label}-${item.color}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 0.5,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: item.color,
                border: 1,
                borderColor: 'divider',
                borderRadius: 0.5,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {item.label}
            </Typography>
            {item.value !== undefined && item.value !== item.label && (
              <Typography variant="body2" color="text.secondary">
                {item.value}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default DraggableLegend;
